import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Card,
    Input,
    Select,
    Button,
    InputNumber,
    DatePicker,
    Table,
    Empty,
    Spin,
    message,
    notification,
    Checkbox,
    Tag,
} from "antd";
import {
    EnvironmentOutlined,
    PlusOutlined,
    DeleteOutlined,
    CheckCircleFilled,
    LoadingOutlined,
    UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Layout from "../../Layout";
import { getBranches, BranchItem } from "../../api/Branch";
import { mixGetV1, mixPostV1 } from "../../services/mixin";
import useAuthStore from "../../store/authStore";
import { useLocationData } from "../../hooks/useLocationData";
import { useDebounce } from "../../hooks/useDebounce";
import {
    getChannelCustomers,
    getChannelCustomerDetail,
    getCustomerDetail,
    updateCustomer,
    ChannelCustomerItem,
} from "../../api/ChannelCustomer";

const { TextArea } = Input;

export default function CreateOrderPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { search } = useLocation();
    const { isSuperAdmin, hasPermission } = useAuthStore();

    // ── form state ──────────────────────────────────────────
    const [customerName, setCustomerName] = useState("");
    /** Họ tên thật trên hồ sơ khách (customer_name của lịch hẹn là tên hiển thị) */
    const [customerFullname, setCustomerFullname] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerGender, setCustomerGender] = useState<number | null>(null);
    const [customerDob, setCustomerDob] = useState<dayjs.Dayjs | null>(null);
    const [channelCustomerId, setChannelCustomerId] = useState<number | null>(null);
    // Kênh liên kết của khách (tự gắn khi chọn khách) -> gửi channel_connection_id
    const [channelConnectionId, setChannelConnectionId] = useState<number | null>(null);
    const [customerSearch, setCustomerSearch] = useState("");
    const [provinceCode, setProvinceCode] = useState<string | null>(null);
    const [wardCode, setWardCode] = useState<string | null>(null);
    // Khách nước ngoài (is_foreign = 1 trên hồ sơ khách) không có Tỉnh/TP - Xã/Phường
    const [isForeignCustomer, setIsForeignCustomer] = useState(false);
    // Giá trị gốc lấy từ hồ sơ khách, dùng để biết có cần lưu ngược lại hồ sơ hay không
    const initialCustomerProfile = useRef({ fullname: "", isForeign: false });
    const [address, setAddress] = useState("");
    const [scheduledAt, setScheduledAt] = useState<dayjs.Dayjs | null>(null);
    const [branchId, setBranchId] = useState<number | null>(null);
    const [branchStaffId, setBranchStaffId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number | null>(null);
    const [telesaleId, setTelesaleId] = useState<number | null>(null);
    const [selectedServices, setSelectedServices] = useState<any[]>([]);
    const [appointmentNote, setAppointmentNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // ── option lists ─────────────────────────────────────────
    const [branches, setBranches] = useState<{ label: string; value: number }[]>([]);
    const [customerOptions, setCustomerOptions] = useState<{ label: string; value: number }[]>([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingCustomerDetail, setLoadingCustomerDetail] = useState(false);
    const [branchStaffOptions, setBranchStaffOptions] = useState<{ label: string; value: number }[]>([]);
    const [loadingBranchStaff, setLoadingBranchStaff] = useState(false);
    const [staffOptions, setStaffOptions] = useState<{ label: string; value: number }[]>([]);
    const [telesaleOptions, setTelesaleOptions] = useState<{ label: string; value: number }[]>([]);

    // ── service search dropdown ───────────────────────────────
    const [serviceList, setServiceList] = useState<any[]>([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [showServiceDropdown, setShowServiceDropdown] = useState(false);
    const [searchService, setSearchService] = useState("");
    const debouncedSearch = useDebounce(searchService, 400);
    const debouncedCustomerSearch = useDebounce(customerSearch, 350);
    const serviceSearchRef = useRef<any>(null);

    // ── location ──────────────────────────────────────────────
    const { provinceOptions, wardOptions, loadingProvinces, loadingWards, loadWards } = useLocationData();

    // ── computed totals ───────────────────────────────────────
    const serviceTotal = selectedServices.reduce(
        (sum: number, s: any) => sum + (s.price || 0) * (s.qty || 1),
        0
    );
    const totalAmount = serviceTotal;
    const isCustomerSelected = !!channelCustomerId;
    const isFormLocked = !isCustomerSelected || loadingCustomerDetail;

    // ── fetch staff ───────────────────────────────────────────────────
    const fetchBranchStaff = useCallback(async () => {
        setLoadingBranchStaff(true);
        try {
            const toOpt = (s: any) => ({ label: s.name, value: s.id });
            const [resPage, resTele, resBranch] = await Promise.all([
                mixGetV1('/api/v1/be/staffs', { type_user: 1, type_department_id: 2, limit: 200 }),
                mixGetV1('/api/v1/be/staffs', { type_user: 1, type_department_id: 3, limit: 200 }),
                mixGetV1('/api/v1/be/staffs', { type_user: 1, type_department_id: 4, limit: 200 }),
            ]);
            if (resPage?.status && resPage.data?.items) setStaffOptions(resPage.data.items.map(toOpt));
            if (resTele?.status && resTele.data?.items) setTelesaleOptions(resTele.data.items.map(toOpt));
            if (resBranch?.status && resBranch.data?.items) setBranchStaffOptions(resBranch.data.items.map(toOpt));
        } catch (e) {
            console.error('Failed to fetch staff:', e);
        } finally {
            setLoadingBranchStaff(false);
        }
    }, []);

    // ── fetch branches ────────────────────────────────────────
    const fetchBranches = useCallback(async () => {
        try {
            const res = await getBranches();
            if (res?.status && res.data?.items) {
                setBranches(res.data.items.map((b: BranchItem) => ({ label: b.name, value: b.id })));
            } else if (res?.status && Array.isArray(res.data)) {
                setBranches((res.data as BranchItem[]).map((b) => ({ label: b.name, value: b.id })));
            }
        } catch (e) {
            console.error("Failed to fetch branches:", e);
        }
    }, []);

    // ── fetch customers for searchable select ─────────────────
    const fetchCustomers = useCallback(async (search?: string) => {
        setLoadingCustomers(true);
        try {
            const res = await mixGetV1('/api/v1/be/customers', {
                search: search || undefined,
                limit: 50,
                sort: 'id,desc',
            });
            if (res?.status && res.data?.items) {
                setCustomerOptions(
                    res.data.items.map((item: any) => ({
                        value: item.id,
                        label: item.display_name || item.phone || `#${item.id}`,
                    }))
                );
            } else {
                setCustomerOptions([]);
            }
        } catch (e) {
            console.error("Failed to fetch customers:", e);
            setCustomerOptions([]);
        } finally {
            setLoadingCustomers(false);
        }
    }, []);

    const handleSelectCustomer = useCallback(async (id: number) => {
        setChannelCustomerId(id);
        setLoadingCustomerDetail(true);
        try {
            const res = await getCustomerDetail(id);
            if (!res?.status || !res.data) return;
            const detail = res.data;
            setCustomerName(detail.display_name || "");
            setCustomerPhone(detail.phone || "");
            setCustomerEmail(detail.email || "");
            setCustomerGender(detail.gender ?? null);
            const fullname = detail.fullname || "";
            const foreign = Number((detail as any).is_foreign) === 1;
            setCustomerFullname(fullname);
            setIsForeignCustomer(foreign);
            initialCustomerProfile.current = { fullname, isForeign: foreign };
            setCustomerDob(detail.birthday ? dayjs(detail.birthday) : null);
            setAddress(detail.address || "");
            setProvinceCode(detail.province_code || null);
            // Tự gắn kênh liên kết đầu tiên của khách để gửi channel_connection_id
            const conns = (detail.connections || []) as any[];
            const conn = conns.find((c) => c?.channel_connection_id || c?.channel_connection?.id || c?.channel_customer?.channel_connection_id);
            const ccId = conn?.channel_connection_id ?? conn?.channel_connection?.id ?? conn?.channel_customer?.channel_connection_id ?? null;
            setChannelConnectionId(ccId ? Number(ccId) : null);
            if ((detail as any).staff_id) setStaffId(Number((detail as any).staff_id));
            if ((detail as any).telesale_id) setTelesaleId(Number((detail as any).telesale_id));
            setCustomerOptions((prev) => {
                const exists = prev.some((opt) => opt.value === id);
                if (exists) return prev;
                return [{ value: id, label: detail.display_name || `#${id}` }, ...prev];
            });
            if (detail.province_code) {
                await loadWards(detail.province_code);
            }
            setWardCode(detail.ward_code || null);
        } finally {
            setLoadingCustomerDetail(false);
        }
    }, [loadWards]);

    // ── fetch service list ────────────────────────────────────
    const fetchServiceList = useCallback(async (search?: string) => {
        setLoadingServices(true);
        try {
            const params: any = { take: 15, status: 0 };
            if (search) params.search = search;
            const res = await mixGetV1("/api/v1/be/services", params);
            if (res?.status && res.data?.items) {
                setServiceList(res.data.items);
            }
        } catch (e) {
            console.error("Failed to fetch services:", e);
        } finally {
            setLoadingServices(false);
        }
    }, []);

    useEffect(() => {
        fetchBranches();
        fetchCustomers();
        fetchBranchStaff();
    }, [fetchBranches, fetchCustomers, fetchBranchStaff]);

    useEffect(() => {
        if (showServiceDropdown) {
            fetchServiceList(debouncedSearch || undefined);
        }
    }, [showServiceDropdown, debouncedSearch, fetchServiceList]);

    useEffect(() => {
        fetchCustomers(debouncedCustomerSearch || undefined);
    }, [debouncedCustomerSearch, fetchCustomers]);

    useEffect(() => {
        const paramId = Number(new URLSearchParams(search).get("customer_id") || "0");
        if (!Number.isFinite(paramId) || paramId <= 0) return;
        handleSelectCustomer(paramId);
    }, [search, handleSelectCustomer]);

    // ── service dropdown helpers ──────────────────────────────
    const handleToggleServiceDropdown = () => {
        const next = !showServiceDropdown;
        setShowServiceDropdown(next);
        if (next) {
            setSearchService("");
            setTimeout(() => serviceSearchRef.current?.focus(), 100);
        }
    };

    const addService = (svc: any) => {
        const exist = selectedServices.find((s) => s.id === svc.id);
        if (exist) {
            setSelectedServices(
                selectedServices.map((s) => (s.id === svc.id ? { ...s, qty: s.qty + 1 } : s))
            );
        } else {
            setSelectedServices([
                ...selectedServices,
                { id: svc.id, name: svc.name, price: Number(svc.price) || 0, qty: 1 },
            ]);
        }
        setShowServiceDropdown(false);
        setSearchService("");
    };

    const updateServiceQty = (id: number, qty: number) => {
        setSelectedServices(selectedServices.map((s) => (s.id === id ? { ...s, qty } : s)));
    };

    const removeService = (id: number) => {
        setSelectedServices(selectedServices.filter((s) => s.id !== id));
    };

    const extractApiErrorMessage = (source: any, fallback: string) => {
        const pick = (value: any): string | null => {
            if (!value) return null;
            if (typeof value === "string") {
                const trimmed = value.trim();
                return trimmed || null;
            }
            if (Array.isArray(value)) {
                for (const item of value) {
                    const found = pick(item);
                    if (found) return found;
                }
                return null;
            }
            if (typeof value === "object") {
                for (const v of Object.values(value)) {
                    const found = pick(v);
                    if (found) return found;
                }
            }
            return null;
        };

        return (
            pick(source?.error) ||
            pick(source?.errors) ||
            pick(source?.msg) ||
            pick(source?.message) ||
            pick(source?.data?.error) ||
            pick(source?.data?.errors) ||
            pick(source?.data?.msg) ||
            pick(source?.data?.message) ||
            fallback
        );
    };

    const serviceColumns = [
        { title: t("roomchat.product"), dataIndex: "name" },
        {
            title: t("roomchat.quantity"),
            width: 110,
            render: (_: any, record: any) => (
                <InputNumber
                    disabled={isFormLocked}
                    min={1}
                    value={record.qty}
                    onChange={(val: any) => updateServiceQty(record.id, val)}
                />
            ),
        },
        {
            title: t("roomchat.subtotal"),
            width: 130,
            render: (_: any, record: any) =>
                ((record.qty || 1) * (record.price || 0)).toLocaleString() + " đ",
        },
        {
            width: 40,
            render: (_: any, record: any) => (
                <DeleteOutlined
                    className={`text-red-500 ${isFormLocked ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                    onClick={() => !isFormLocked && removeService(record.id)}
                />
            ),
        },
    ];

    // ── submit ────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!channelCustomerId) {
            message.error("Vui lòng chọn khách hàng");
            return;
        }
        if (!customerName.trim()) {
            message.error("Họ và tên là bắt buộc");
            return;
        }
        // Bắt buộc Số điện thoại, Tỉnh/Thành phố, Xã/Phường và Địa chỉ khi tạo lịch hẹn
        const missingFields: string[] = [];
        if (!customerPhone.trim()) missingFields.push(t("create_order.phone"));
        // Khách nước ngoài: bỏ qua địa giới trong nước, chỉ cần Địa chỉ
        if (!isForeignCustomer && !provinceCode) missingFields.push(t("create_order.province"));
        if (!isForeignCustomer && !wardCode) missingFields.push(t("create_order.ward"));
        if (!address.trim()) missingFields.push(t("create_order.address"));
        if (missingFields.length > 0) {
            const fields = missingFields.join(", ");
            message.warning(
                t("roomchat.appointment_need_location", { fields })
                || `Vui lòng cập nhật ${fields} của khách hàng để tạo lịch hẹn`
            );
            return;
        }
        if (!branchId) {
            message.error("Vui lòng chọn chi nhánh");
            return;
        }

        setSubmitting(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
            const currentUserId = Number(currentUser?.id || 0);
            const canAssignOthers = isSuperAdmin || hasPermission("appointment.assign");

            // Họ tên thật và Nguồn gốc khách thuộc hồ sơ khách hàng (API lịch hẹn không
            // nhận fullname) -> có sửa thì lưu về hồ sơ trước khi tạo lịch hẹn.
            const profile = initialCustomerProfile.current;
            const fullnameChanged = customerFullname.trim() !== profile.fullname.trim();
            const foreignChanged = isForeignCustomer !== profile.isForeign;
            if (fullnameChanged || foreignChanged) {
                const resProfile = await updateCustomer(channelCustomerId, {
                    fullname: customerFullname.trim() || null,
                    is_foreign: isForeignCustomer ? 1 : 0,
                });
                if (resProfile?.status) {
                    initialCustomerProfile.current = {
                        fullname: customerFullname.trim(),
                        isForeign: isForeignCustomer,
                    };
                } else {
                    message.warning("Không lưu được Họ tên thật / Nguồn gốc khách vào hồ sơ khách hàng");
                }
            }

            const payload: any = {
                customer_name: customerName,
                branch_id: branchId,
                status: 1,
                // Khách nước ngoài: báo cho API biết để không bắt buộc Tỉnh/Thành - Phường/Xã
                is_foreign: isForeignCustomer ? 1 : 0,
            };
            if (customerPhone) payload.customer_phone = customerPhone;
            if (customerEmail) payload.customer_email = customerEmail;
            if (customerGender) payload.customer_gender = customerGender;
            if (customerDob) payload.customer_dob = customerDob.format("YYYY-MM-DD");
            if (channelCustomerId) payload.customer_id = channelCustomerId;
            if (channelConnectionId) payload.channel_connection_id = channelConnectionId;
            if (provinceCode) payload.province_code = provinceCode;
            if (wardCode) payload.ward_code = wardCode;
            if (address) payload.address = address;
            if (scheduledAt) payload.scheduled_at = scheduledAt.format("YYYY-MM-DD HH:mm:ss");
            if (selectedServices.length > 0) payload.service_ids = selectedServices.map((s) => s.id);
            if (appointmentNote) payload.note = appointmentNote;
            if (branchStaffId) payload.branch_staff_id = branchStaffId;
            if (telesaleId) payload.telesale_id = telesaleId;
            if (staffId) {
                payload.staff_id = staffId;
            } else if (!canAssignOthers && Number.isFinite(currentUserId) && currentUserId > 0) {
                payload.staff_id = currentUserId;
            }

            const res = await mixPostV1("/api/v1/be/appointments", payload);
            if (res?.status) {
                const createdId = Number(
                    res?.data?.id ??
                    res?.data?.appointment_id ??
                    res?.data?.appointment?.id ??
                    0
                );
                notification.success({
                    message: "Tạo lịch hẹn thành công",
                    description: res.data?.code ? `Mã lịch hẹn: ${res.data.code}` : undefined,
                    placement: "topRight",
                    icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
                });
                setCustomerName("");
                setCustomerFullname("");
                initialCustomerProfile.current = { fullname: "", isForeign: false };
                setCustomerPhone("");
                setCustomerEmail("");
                setCustomerGender(null);
                setCustomerDob(null);
                setChannelCustomerId(null);
                setChannelConnectionId(null);
                setCustomerSearch("");
                setProvinceCode(null);
                setWardCode(null);
                setIsForeignCustomer(false);
                setAddress("");
                setScheduledAt(null);
                setBranchId(null);
                setBranchStaffId(null);
                setStaffId(null);
                setTelesaleId(null);
                setSelectedServices([]);
                setAppointmentNote("");
                if (createdId > 0) {
                    navigate(`/appointment/detail/${createdId}`);
                }
            } else {
                message.error(extractApiErrorMessage(res, "Tạo lịch hẹn thất bại"));
            }
        } catch (e: any) {
            message.error(extractApiErrorMessage(e, "Tạo lịch hẹn thất bại"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>{t("page.create_order")} | Khothi</title>
            </Helmet>

            <div className="">
                <Spin spinning={loadingCustomerDetail} tip="Đang tải thông tin khách hàng...">
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">

                    {/* ── LEFT: Customer info ── */}
                    <Card
                        className="xl:col-span-1 col-span-1 dark:bg-[#141414] dark:border-[#303030]"
                        title={
                            <span className="font-medium text-000000e0">
                                {t("create_order.customer")}
                            </span>
                        }
                    >
                        <div className="space-y-4">

                            <LeftRow label="Khách hàng">
                                <Select
                                    className="w-full"
                                    showSearch
                                    allowClear
                                    value={channelCustomerId}
                                    loading={loadingCustomers || loadingCustomerDetail}
                                    options={customerOptions}
                                    placeholder="Tìm và chọn khách hàng"
                                    filterOption={false}
                                    onSearch={setCustomerSearch}
                                    onChange={(val) => {
                                        if (!val) {
                                            setChannelCustomerId(null);
                                            setChannelConnectionId(null);
                                            setCustomerName("");
                                            setCustomerFullname("");
                                            initialCustomerProfile.current = { fullname: "", isForeign: false };
                                            setCustomerPhone("");
                                            setCustomerEmail("");
                                            setCustomerGender(null);
                                            setCustomerDob(null);
                                            setProvinceCode(null);
                                            setWardCode(null);
                                            setIsForeignCustomer(false);
                                            setAddress("");
                                            setStaffId(null);
                                            setTelesaleId(null);
                                            return;
                                        }
                                        handleSelectCustomer(Number(val));
                                    }}
                                    suffixIcon={<UserOutlined />}
                                    notFoundContent={(loadingCustomers || loadingCustomerDetail) ? <Spin size="small" /> : null}
                                />
                            </LeftRow>

                            <LeftRow required label={t("create_order.full_name")}>
                                <Input
                                    disabled={isFormLocked}
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    placeholder={t("create_order.full_name")}
                                />
                            </LeftRow>

                            {/* Họ tên thật thuộc hồ sơ khách (API lịch hẹn không nhận trường này)
                                -> sửa ở đây sẽ được lưu về hồ sơ khách khi bấm tạo lịch hẹn */}
                            <LeftRow label={t("cd.fullname_real") || "Họ tên thật"}>
                                <Input
                                    disabled={isFormLocked}
                                    value={customerFullname}
                                    onChange={(e) => setCustomerFullname(e.target.value)}
                                    placeholder={t("cd.fullname_real") || "Họ tên thật"}
                                />
                            </LeftRow>

                            <LeftRow required label={t("create_order.phone")}>
                                <Input
                                    disabled={isFormLocked}
                                    value={customerPhone}
                                    onChange={(e) => setCustomerPhone(e.target.value)}
                                    placeholder={t("create_order.phone")}
                                />
                            </LeftRow>

                            <LeftRow label="Email">
                                <Input
                                    disabled={isFormLocked}
                                    value={customerEmail}
                                    onChange={(e) => setCustomerEmail(e.target.value)}
                                    placeholder="Email khách hàng"
                                />
                            </LeftRow>

                            <LeftRow label="Giới tính">
                                <Select
                                    className="w-full"
                                    disabled={isFormLocked}
                                    value={customerGender ?? undefined}
                                    onChange={(v) => setCustomerGender(v ?? null)}
                                    allowClear
                                    // Hồ sơ khách dùng gender = 0 cho "chưa xác định"; thiếu lựa
                                    // chọn này thì Select đổ ra số 0 thay vì nhãn đọc được.
                                    options={[
                                        { label: t("roomchat.gender_unknown") || "Không xác định", value: 0 },
                                        { label: "Nam", value: 1 },
                                        { label: "Nữ", value: 2 },
                                        { label: "Khác", value: 3 },
                                    ]}
                                    placeholder="Chọn giới tính"
                                />
                            </LeftRow>

                            <LeftRow label="Ngày sinh">
                                <DatePicker
                                    className="w-full"
                                    disabled={isFormLocked}
                                    value={customerDob}
                                    onChange={setCustomerDob}
                                    format="DD/MM/YYYY"
                                    placeholder="DD/MM/YYYY"
                                />
                            </LeftRow>

                            {/* Nguồn gốc khách (is_foreign): tick vào thì Tỉnh/Thành - Phường/Xã
                                không còn bắt buộc, và lịch hẹn được gửi kèm is_foreign = 1 */}
                            <LeftRow label={t("cd.customer_origin") || "Nguồn gốc khách"}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Checkbox
                                        disabled={isFormLocked}
                                        checked={isForeignCustomer}
                                        onChange={(e) => setIsForeignCustomer(e.target.checked)}
                                    >
                                        {t("cd.foreign_customer") || "Khách nước ngoài"}
                                    </Checkbox>
                                    {isForeignCustomer && (
                                        <Tag color="volcano" className="mr-0">
                                            {t("cd.origin_foreign") || "Nước ngoài"}
                                        </Tag>
                                    )}
                                </div>
                            </LeftRow>

                            <LeftRow required={!isForeignCustomer} label={t("create_order.province")}>
                                <Select
                                    className="w-full"
                                    disabled={isFormLocked}
                                    value={provinceCode}
                                    loading={loadingProvinces}
                                    onChange={(val) => {
                                        setProvinceCode(val);
                                        setWardCode(null);
                                        if (val) loadWards(val);
                                    }}
                                    options={provinceOptions}
                                    placeholder={t("create_order.please_select")}
                                    showSearch
                                    allowClear
                                    filterOption={(input, option) =>
                                        (option?.label as string)
                                            ?.toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                />
                            </LeftRow>

                            <LeftRow required={!isForeignCustomer} label={t("create_order.ward")}>
                                <Select
                                    className="w-full"
                                    value={wardCode}
                                    loading={loadingWards}
                                    onChange={setWardCode}
                                    options={wardOptions}
                                    placeholder={t("create_order.please_select")}
                                    showSearch
                                    allowClear
                                    disabled={isFormLocked || !provinceCode}
                                />
                            </LeftRow>

                            <LeftRow required label={t("create_order.address")}>
                                <Input
                                    disabled={isFormLocked}
                                    prefix={
                                        <EnvironmentOutlined className="text-gray-400 dark:text-gray-500" />
                                    }
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    placeholder={t("create_order.address")}
                                    className="dark:bg-[#141414] dark:border-[#303030] text-000000e0"
                                />
                            </LeftRow>

                        </div>
                    </Card>

                    {/* ── RIGHT: Services + Cost ── */}
                    <div className="xl:col-span-2 col-span-1 space-y-6">
                        <Card className="dark:bg-[#141414] dark:border-[#303030]">
                            <div className="space-y-5 mb-4">

                                {/* Branch + Staff grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block mb-1 text-gray-700 text-000000e0 font-medium">Chi nhánh:</label>
                                        <Select
                                            disabled={isFormLocked}
                                            value={branchId}
                                            onChange={setBranchId}
                                            className="w-full"
                                            options={branches}
                                            placeholder={t("create_order.please_select")}
                                            showSearch
                                            allowClear
                                            filterOption={(input, option) =>
                                                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-gray-700 text-000000e0 font-medium">Thời gian hẹn:</label>
                                        <DatePicker
                                            showTime
                                            disabled={isFormLocked}
                                            className="w-full"
                                            value={scheduledAt}
                                            onChange={setScheduledAt}
                                            format="DD/MM/YYYY HH:mm"
                                            placeholder="DD/MM/YYYY HH:mm"
                                        />
                                    </div>
                                    <div className="col-span-full grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block mb-1 text-gray-700 text-000000e0 font-medium">Nhân viên trực page:</label>
                                            <Select
                                                disabled={isFormLocked}
                                                loading={loadingBranchStaff}
                                                value={staffId}
                                                onChange={setStaffId}
                                                className="w-full"
                                                options={staffOptions}
                                                placeholder={t("create_order.please_select")}
                                                showSearch
                                                allowClear
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-gray-700 text-000000e0 font-medium">Nhân viên telesale:</label>
                                            <Select
                                                disabled={isFormLocked}
                                                loading={loadingBranchStaff}
                                                value={telesaleId}
                                                onChange={setTelesaleId}
                                                className="w-full"
                                                options={telesaleOptions}
                                                placeholder={t("create_order.please_select")}
                                                showSearch
                                                allowClear
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-gray-700 text-000000e0 font-medium">Nhân viên cơ sở:</label>
                                            <Select
                                                disabled={isFormLocked}
                                                loading={loadingBranchStaff}
                                                value={branchStaffId}
                                                onChange={setBranchStaffId}
                                                className="w-full"
                                                options={branchStaffOptions}
                                                placeholder={t("create_order.please_select")}
                                                showSearch
                                                allowClear
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Service table + search */}
                                <div className="space-y-3">
                                    <Table
                                        columns={serviceColumns}
                                        dataSource={selectedServices}
                                        rowKey="id"
                                        pagination={false}
                                        size="small"
                                        locale={{
                                            emptyText: (
                                                <Empty description="Chưa có dịch vụ" />
                                            ),
                                        }}
                                        className="custom-ant-table"
                                        scroll={{ x: "max-content" }}
                                    />

                                    <div className="flex justify-end relative">
                                        <Button
                                            disabled={isFormLocked}
                                            icon={<PlusOutlined />}
                                            onClick={handleToggleServiceDropdown}
                                        >
                                            Thêm dịch vụ
                                        </Button>

                                        {showServiceDropdown && (
                                            <div className="absolute top-[40px] right-0 w-[320px] bg-white dark:bg-[#1f1f1f] border rounded-lg shadow-lg p-2 z-50">
                                                <Input
                                                    ref={serviceSearchRef}
                                                    placeholder="Tìm dịch vụ..."
                                                    value={searchService}
                                                    onChange={(e) =>
                                                        setSearchService(e.target.value)
                                                    }
                                                    allowClear
                                                    className="mb-2"
                                                />
                                                <div className="max-h-[280px] overflow-y-auto">
                                                    {loadingServices ? (
                                                        <div className="flex justify-center py-6">
                                                            <Spin
                                                                indicator={
                                                                    <LoadingOutlined spin />
                                                                }
                                                            />
                                                        </div>
                                                    ) : serviceList.length > 0 ? (
                                                        serviceList.map((svc) => (
                                                            <div
                                                                key={svc.id}
                                                                onClick={() => !isFormLocked && addService(svc)}
                                                                className="flex gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                                                            >
                                                                <span className="flex-1 line-clamp-2 text-sm">
                                                                    {svc.name}
                                                                </span>
                                                                <span className="whitespace-nowrap text-sm font-medium">
                                                                    {Number(
                                                                        svc.price || 0
                                                                    ).toLocaleString()}
                                                                </span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="text-center text-gray-400 py-4">
                                                            Không có dữ liệu
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Cost box */}
                                <div className="bg-gray-50 dark:bg-[#1f1f1f] rounded-lg p-5 border border-gray-200 dark:border-[#303030]">
                                    <div className="flex items-center gap-3 mb-5">
                                        <span className="font-medium text-000000e0">
                                            {t("create_order.cost_info")}
                                        </span>
                                    </div>

                                    <FormRow label={t("create_order.total_amount")}>
                                        <Input
                                            value={totalAmount.toLocaleString()}
                                            readOnly
                                            className="bg-gray-50 dark:bg-[#1a1a1a]"
                                        />
                                    </FormRow>

                                    <FormRow label={t("create_order.order_note")}>
                                        <TextArea
                                            disabled={isFormLocked}
                                            rows={3}
                                            value={appointmentNote}
                                            onChange={(e) => setAppointmentNote(e.target.value)}
                                        />
                                    </FormRow>
                                </div>
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
                                <Button
                                    type="primary"
                                    disabled={isFormLocked}
                                    loading={submitting}
                                    onClick={handleSubmit}
                                    className="!bg-[#3e91ab] !border-[#3e91ab] hover:!bg-[#2d7a92] w-full sm:w-auto"
                                >
                                    {t("create_order.create_order")}
                                </Button>
                            </div>
                        </Card>
                    </div>

                    </div>
                </Spin>
            </div>
        </Layout>
    );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function LeftRow({
    label,
    required,
    children,
}: {
    label: string;
    required?: boolean;
    children: ReactNode;
}) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <label className="sm:w-[140px] sm:text-right text-gray-700 text-000000e0">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}:
            </label>
            <div
                className="flex-1
                    dark:[&_.ant-input]:bg-[#141414] dark:[&_.ant-input]:border-[#303030] dark:[&_.ant-input]:text-gray-200
                    dark:[&_.ant-select-selector]:bg-[#141414] dark:[&_.ant-select-selector]:border-[#303030] dark:[&_.ant-select-selector]:text-gray-200
                    dark:[&_.ant-picker]:bg-[#141414] dark:[&_.ant-picker]:border-[#303030] dark:[&_.ant-picker-input>input]:text-gray-200"
            >
                {children}
            </div>
        </div>
    );
}

function FormRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6 mb-4">
            <label className="sm:w-[200px] sm:text-right pt-1 text-gray-700 text-000000e0">
                {label}:
            </label>
            <div
                className="flex-1
                    dark:[&_.ant-input]:bg-[#141414] dark:[&_.ant-input]:border-[#303030] dark:[&_.ant-input]:text-gray-200
                    dark:[&_.ant-select-selector]:bg-[#141414] dark:[&_.ant-select-selector]:border-[#303030] dark:[&_.ant-select-selector]:text-gray-200
                    dark:[&_.ant-input-number]:bg-[#141414] dark:[&_.ant-input-number]:border-[#303030] dark:[&_.ant-input-number-input]:text-gray-200"
            >
                {children}
            </div>
        </div>
    );
}
